import { ChevronDown } from '@carbon/icons-react';
import { UpstreamEvent } from 'client';
import React from 'react';
import { useSearchParams } from 'react-router';
import { DefaultMultiSelectFilter } from 'routes/components/default-multi-select-filter';
import { useFilterFormListener } from 'routes/components/filter-form-context';
import { PillButton } from 'routes/components/pill-button';
import { SelectFilterOption } from 'routes/components/select-filter-base';
import { useMultiSelect } from 'routes/hooks/use-select-strategy';

import { getFilterParamKeys } from 'DesignSystem/features/table-context/filter-param';
import { Button } from 'DesignSystem/ui/button';
import { FilterComponentProps } from 'types/react-table';

import { EVENT_TYPE_OPTIONS } from '../../utils';

const options = EVENT_TYPE_OPTIONS;

export function EventTypeFilter(props: FilterComponentProps<UpstreamEvent>) {
  const [searchParams] = useSearchParams();
  if (!props.meta?.filter) return null;
  const paramKeys = getFilterParamKeys(props.meta.filter.param);

  return (
    <EventTypeSelect
      name={paramKeys[0] ?? ''}
      defaultValue={options.filter((option) => searchParams.getAll(paramKeys[0] ?? '').includes(option.value))}
    >
      {({ selectedOptions }) => {
        return (
          <Button variant="outline" className="w-full justify-between overflow-hidden">
            <span>{selectedOptions.length > 0 ? `${selectedOptions.length} selected` : 'All'}</span>
            <ChevronDown />
          </Button>
        );
      }}
    </EventTypeSelect>
  );
}

export function EventTypeFilterPill(props: FilterComponentProps<UpstreamEvent>) {
  const [searchParams] = useSearchParams();
  if (!props.meta?.filter) return null;
  const paramKeys = getFilterParamKeys(props.meta.filter.param);

  return (
    <EventTypeSelect
      name={paramKeys[0] ?? ''}
      defaultValue={options.filter((option) => searchParams.getAll(paramKeys[0] ?? '').includes(option.value))}
      onOpenChange={(open) => {
        if (!open) {
          props.formRef?.current?.requestSubmit();
        }
      }}
    >
      {({ selectedOptions }) => (
        <PillButton
          label={props.meta?.label ?? ''}
          selectedOptions={selectedOptions}
          pinned={props.meta?.filter?.pinned}
          param={paramKeys[0] ?? ''}
        />
      )}
    </EventTypeSelect>
  );
}

function EventTypeSelect(
  props: Pick<React.ComponentProps<typeof DefaultMultiSelectFilter>, 'onOpenChange' | 'onSelect' | 'onInput'> & {
    name: string;
    defaultValue: SelectFilterOption[];
    children: (props: { selectedOptions: SelectFilterOption[] }) => React.ReactNode;
  }
) {
  const [selectedOptions, update, reset] = useMultiSelect({
    defaultValue: props.defaultValue,
  });

  useFilterFormListener(
    React.useCallback(
      (event) => {
        if (event.type === 'CLEAR') {
          update([]);
        } else if (event.type === 'RESET') {
          reset();
        }
      },
      [update, reset]
    )
  );

  return (
    <DefaultMultiSelectFilter
      name={props.name}
      value={selectedOptions.map((option) => option.value)}
      options={options.map((option) => ({ ...option, keywords: [option.label, option.value] }))}
      onOpenChange={props.onOpenChange}
      onSelect={(value) => {
        if (value.trim().length === 0) return;
        const selectedOption = options.find((option) => option.value === value);
        if (!selectedOption) return;

        const isSelected = selectedOptions.some((opt) => opt.value === value);
        if (isSelected) {
          update(selectedOptions.filter((opt) => opt.value !== value));
        } else {
          update([...selectedOptions, selectedOption]);
        }
        props.onSelect?.(value);
      }}
    >
      {props.children({
        selectedOptions,
      })}
    </DefaultMultiSelectFilter>
  );
}
